import * as React from 'react';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { getCatalog, getShouldShowLive } from '@/redux/modules/catalog';
import { getCatalogUrl, getSellerUrl } from '@/utils/urls';
import { useDispatch, useSelector } from 'react-redux';
import Address from '@liveauctioneers/caterwaul-components/lib/Address/Address';
import BidConsoleOpener from '@/components/BidConsoleOpener/BidConsoleOpener';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import { catalogCardDate } from '@/utils/dates';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import clamp from 'lodash/clamp';
import classNames from 'classnames';
import ConsoleSocial from '@/components/ConsoleSocial/ConsoleSocial';
import CoverLotsComponent from '@/components/CoverLots/CoverLotsComponent';
import FollowSellerContainer from '@/components/FollowSeller/FollowSellerContainer';
import { FormattedMessage } from 'react-intl';
import { getAddressForCatalog } from '@/utils/address';
import { getCoverLots } from '@/redux/modules/coverLots';
import { getLiveCatalogData } from '@/redux/modules/console';
import { getPreviewCatalogCardsData } from '@/redux/modules/cardData';
import { getSeller } from '@/redux/modules/seller';
import { hasPubSubConfig } from '@/redux/modules/config';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import { isTrendingCatalog } from '@/redux/modules/upcomingCatalogs';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import noop from '@/utils/noop';
import OverlayTrigger from '@/components/Overlay/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import PubnubCatalogContainer from '@/components/PubnubContainer/PubnubCatalogContainer';
import ReactCountryFlag from 'react-country-flag';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import { safelyRender } from '@/utils/safelyRender';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { showCountDownBasedOnMinimumDate } from '@/utils/countDown';
import styles from './CatalogCard.module.scss';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import usePrevious from '@/hooks/usePrevious';

type Props = {
    catalogId: number;
    className?: string;
    featured?: boolean;
    followed?: boolean;
    hideWhenDone?: boolean;
    homepage?: boolean;
    horizontal?: boolean;
    preview?: boolean;
    previewSellerId?: number;
    showSendSellerConsignmentMessageModal?: Function;
    trackClick: (event: React.SyntheticEvent) => void;
};

const limitDescription = (description: string) => {
    if (description && description.length > 139) {
        description = `${description.slice(0, 139).trim()}&mldr;`;
    }
    return safelyRender(description, 'div');
};

const getNormalizedCountryCode = (code: string) => {
    switch (code) {
        case 'uk':
            return 'gb';
        default:
            return code;
    }
};

const CatalogCard = (props: Props) => {
    const {
        catalogId,
        className = '',
        featured = false,
        followed,
        hideWhenDone,
        homepage,
        horizontal = false,
        preview = false,
        previewSellerId,
        showSendSellerConsignmentMessageModal,
        trackClick = noop,
    } = props;

    const {
        catalog,
        catalogStatus,
        coverLots,
        currentItemIndex,
        isCatalogOnly,
        lotsListed,
        saleStartTs,
        seller,
        sellerId,
        showLive = false,
        trending = false,
    } = useSelector((state) => {
        if (!preview) {
            const catalog = getCatalog(state, catalogId);
            const coverLots = getCoverLots(state, catalogId);
            const seller = getSeller(state, catalog.sellerId);
            const { catalogStatus, isCatalogOnly, lotsListed, saleStartTs } = catalog;
            const { currentItemIndex } = getLiveCatalogData(state, catalogId);
            const trending = isTrendingCatalog(state, catalogId);
            const showLive = getShouldShowLive(state, catalogId);

            return {
                catalog,
                catalogStatus,
                coverLots,
                currentItemIndex,
                isCatalogOnly,
                lotsListed,
                saleStartTs,
                seller,
                sellerId: catalog.sellerId,
                showLive,
                trending,
            };
        } else {
            const allPreviews = getPreviewCatalogCardsData(state, previewSellerId) || [];
            const { catalogPreview, seller } =
                allPreviews?.filter((previewCatalog) => previewCatalog.catalogPreview.previewId === catalogId)[0] || {};

            return {
                catalog: catalogPreview,
                saleStartTs: catalogPreview.saleStartTs,
                seller,
                sellerId: previewSellerId,
            };
        }
    }, isEqual);

    let percentComplete = 2.5; // default is set at the lowest we allow;
    // if either of these numbers are undefined, we'll just return the minimum
    if (!Number.isNaN(currentItemIndex) && !Number.isNaN(lotsListed)) {
        percentComplete = clamp(((currentItemIndex + 1) / lotsListed) * 100, 2.5, 100);
    }

    const { description, imagePath, isSEOExcluded, title } = catalog || {};

    const dispatch = useDispatch();
    const prevShowLive = usePrevious(showLive);

    // Fetch live catalog status from auction-engine
    React.useEffect(() => {
        if (!prevShowLive && showLive) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId));
        }
    }, [showLive, prevShowLive, catalogId, homepage, dispatch]);

    // Fetch live catalog presence count if we have the subscribe key from fetching live catalog status
    const shouldFetchCatalogPresence = useSelector(hasPubSubConfig) && showLive;
    React.useEffect(() => {
        if (shouldFetchCatalogPresence) {
            dispatch(fetchCatalogPresence(catalogId));
        }
    }, [catalogId, dispatch, shouldFetchCatalogPresence]);

    if (hideWhenDone && catalogStatus === 'done') {
        return null;
    }

    const { address1, address2, city, countryCode, postalCode, stateCode } = getAddressForCatalog(catalog, seller);

    const { name: sellerName } = seller || {};
    const startDate = catalogCardDate(saleStartTs);
    const catalogUrl = getCatalogUrl(catalogId, title);
    const showDate = !isCatalogOnly && showCountDownBasedOnMinimumDate(saleStartTs);
    const safeDescription = limitDescription(description);
    const safeTitle = safelyRender(title, 'span');
    const cantViewCatalogPopover = (
        // @ts-ignore replace bootstrap
        <Popover bsClass="popover" id={catalogId}>
            <FormattedMessage id="this_catalog_is_an_auction_preview" />
        </Popover>
    );

    const classKey = horizontal ? 'horizontal-catalog-card' : 'catalog-card';
    const classes = classNames(styles[classKey], className, styles['card-body']);
    const coverLotsClasses = featured ? classNames(styles.featured, styles['cover-lots']) : styles['cover-lots'];
    const consoleClass = featured ? classNames(styles.social, styles.featured) : styles.social;
    const coverLotsBlockClasses = horizontal ? styles.left : '';
    const titleStyles = horizontal ? styles.title : classNames(styles.left, styles.title);
    const infoStyles = horizontal ? styles.middle : '';
    const sellerStyles = classNames(styles.left, styles.seller);
    const timeStyles = horizontal ? styles.right : styles.left;
    const showLiveStyles = horizontal
        ? ''
        : featured
        ? classNames(styles.right, styles.bottom)
        : classNames(styles.right, styles.live);

    let innerClass;
    if (horizontal) {
        innerClass = styles['info-box'];
    } else if (featured) {
        innerClass = classNames(styles['card-body'], styles['expanded-preview']);
    } else {
        innerClass = styles['card-body'];
    }

    const location = [city, stateCode].filter((element) => element).join(', ');

    const progressStyle = {
        width: `${percentComplete}%`,
    };
    const linkRel = isSEOExcluded ? 'nofollow' : '';

    const flagCode = getNormalizedCountryCode(countryCode);

    return (
        <>
            {showLive && !homepage && !horizontal && (
                <PubnubCatalogContainer catalogId={catalogId} limitMessagesFilter="catalogCard" />
            )}
            <div className={classes} onClick={trackClick}>
                <div className={coverLotsBlockClasses}>
                    {preview ? (
                        <div className={styles['preview-image-container']}>
                            <ImageWithFallback alt="the image" className={styles.image} src={imagePath} />
                        </div>
                    ) : (
                        <CoverLotsComponent
                            catalogId={catalogId}
                            catalogUrl={catalogUrl}
                            className={coverLotsClasses}
                            coverLots={coverLots}
                            featured={featured}
                            isSEOExcluded={isSEOExcluded}
                            sellerId={sellerId}
                        />
                    )}
                    {showLive && !horizontal && (
                        <>
                            <ConsoleSocial
                                catalogId={catalogId}
                                className={consoleClass}
                                isOnCatalogCard
                                trending={trending}
                            />
                            <div className={styles['bar-container']}>
                                <div className={styles.bar} style={progressStyle} />
                            </div>
                        </>
                    )}
                </div>
                <div className={innerClass}>
                    <div className={infoStyles}>
                        <span className={titleStyles}>
                            {preview ? (
                                horizontal ? (
                                    safeTitle
                                ) : (
                                    <OverlayTrigger
                                        overlay={cantViewCatalogPopover}
                                        placement="top"
                                        rootClose
                                        trigger={['click']}
                                    >
                                        {safeTitle}
                                    </OverlayTrigger>
                                )
                            ) : (
                                <>
                                    <Link
                                        className={featured ? styles['inner-title'] : ''}
                                        primary
                                        rel={linkRel}
                                        route
                                        to={catalogUrl}
                                    >
                                        {safeTitle}
                                    </Link>
                                    {featured && (
                                        <ButtonLink className={styles.explore} rel={linkRel} url={catalogUrl}>
                                            <FormattedMessage id="explore" />
                                        </ButtonLink>
                                    )}
                                </>
                            )}
                        </span>
                        <span className={styles.description}>
                            {horizontal &&
                                (preview ? (
                                    <span>{safeDescription}</span>
                                ) : (
                                    <Link primaryWeak rel={linkRel} route to={catalogUrl}>
                                        {safeDescription}
                                    </Link>
                                ))}
                        </span>
                    </div>
                    {!horizontal && (
                        <div className={sellerStyles}>
                            <Link primary route to={getSellerUrl(sellerId, sellerName)}>
                                {sellerName}
                            </Link>
                        </div>
                    )}
                    <div className={timeStyles}>
                        <div className={styles['start-date']}>
                            <Text caption colorPrimaryWeak>
                                {showLive || catalogStatus === 'done' ? (
                                    <FormattedMessage id="started" values={{ startDate }} />
                                ) : (
                                    <>
                                        <FormattedMessage id="starts" values={{ startDate }} />
                                        {horizontal && !preview && (
                                            <Address
                                                address={{
                                                    address1,
                                                    address2,
                                                    city,
                                                    countryCode,
                                                    postalCode,
                                                    state: stateCode,
                                                }}
                                                cityState
                                                className={styles.address}
                                            />
                                        )}
                                    </>
                                )}
                            </Text>
                        </div>
                        {horizontal && preview && (
                            <>
                                <FollowSellerContainer sellerId={sellerId}>
                                    <Button primary>
                                        {followed ? (
                                            <FormattedMessage id="following_updates" />
                                        ) : (
                                            <FormattedMessage id="follow_updates" />
                                        )}
                                    </Button>
                                </FollowSellerContainer>
                                <div className={styles.consign}>
                                    <ShouldLogInContainer
                                        requireCompleteProfile
                                        requireLogin
                                        runFunction={showSendSellerConsignmentMessageModal}
                                    >
                                        <Link>
                                            <FormattedMessage id="consign_item" />
                                        </Link>
                                    </ShouldLogInContainer>
                                </div>
                                <OverlayTrigger
                                    overlay={cantViewCatalogPopover}
                                    placement="top"
                                    rootClose
                                    trigger={['click', 'hover']}
                                >
                                    <div className={styles['cant-view']}>
                                        <FormattedMessage id="why_cant_i_view_the_catalog" />
                                    </div>
                                </OverlayTrigger>
                            </>
                        )}
                        {!horizontal && !featured && !preview && (
                            <>
                                <ReactCountryFlag
                                    alt={flagCode}
                                    cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/"
                                    className={styles.flag}
                                    countryCode={flagCode}
                                    style={{
                                        height: '13px',
                                        marginTop: '1px',
                                        width: '24px',
                                    }}
                                    svg
                                />
                                <span className={styles.location}>{` ${location}`}</span>
                            </>
                        )}
                        {preview && !horizontal && (
                            <span className={showLiveStyles}>
                                <FollowSellerContainer sellerId={sellerId}>
                                    <Link className={styles['follow-container']}>
                                        {followed ? (
                                            <FormattedMessage id="following_updates" />
                                        ) : (
                                            <FormattedMessage id="follow_updates" />
                                        )}
                                    </Link>
                                </FollowSellerContainer>
                            </span>
                        )}
                        {!preview && (
                            <div className={showLiveStyles}>
                                {horizontal ? (
                                    showLive ? (
                                        <div className={styles['live-link-container']}>
                                            <BidConsoleOpener catalogId={catalogId}>
                                                <span className={styles.live}>
                                                    <Text caption colorError>
                                                        <FormattedMessage id="live_auction" />
                                                    </Text>
                                                </span>
                                            </BidConsoleOpener>
                                        </div>
                                    ) : (
                                        !isCatalogOnly && (
                                            <CatalogDate className={styles.countdown} date={saleStartTs} />
                                        )
                                    )
                                ) : showLive ? (
                                    <BidConsoleOpener catalogId={catalogId}>
                                        <span className={styles.live}>
                                            <Text caption colorError>
                                                <FormattedMessage id="live_auction" />
                                            </Text>
                                        </span>
                                    </BidConsoleOpener>
                                ) : (
                                    <>
                                        {catalogStatus !== 'done' ? (
                                            <CatalogDate
                                                className={styles.countdown}
                                                date={saleStartTs}
                                                showDate={showDate}
                                            />
                                        ) : (
                                            <span className={styles.ended}>
                                                <FormattedMessage id="auction_ended" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        {horizontal && !preview && (
                            <ButtonLink className={styles.explore} url={catalogUrl}>
                                <FormattedMessage id="explore" />
                            </ButtonLink>
                        )}
                        {horizontal && !preview && (
                            <RegisterLink catalogId={catalogId} className={styles['register-link']} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default React.memo<Props>(CatalogCard, isEqual);
